// react
import React, { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HandymanIcon from '@mui/icons-material/Handyman';

// material ui
import { Box, Collapse, Typography } from '@mui/material';

// relative
import { ToolApprovalPrompt } from './ToolApprovalPrompt';

interface ToolCallDisplayProps {
  toolName: string;
  toolInput?: unknown;
  toolOutput?: unknown;
  state?: string;
  approval?: {
    id: string;
  };
  onApprove: (id: string) => void;
  onDeny: (id: string) => void;
}

export const ToolCallDisplay: React.FC<ToolCallDisplayProps> = ({
  toolName,
  toolInput,
  toolOutput,
  state,
  approval,
  onApprove,
  onDeny,
}) => {
  const { t } = useTranslation('ai-assistant');
  const needsApproval = state === 'approval-requested' && approval;
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        mt: 1.5,
        borderRadius: 2,
        overflow: 'hidden',
        border: (theme) =>
          `1px solid ${
            needsApproval
              ? theme.palette.warning.main
              : theme.palette.mode === 'light'
                ? theme.palette.grey[300]
                : theme.palette.grey[700]
          }`,
        bgcolor: (theme) =>
          needsApproval
            ? theme.palette.mode === 'light'
              ? 'warning.lightest'
              : 'rgba(255, 152, 0, 0.1)'
            : theme.palette.mode === 'light'
              ? 'grey.50'
              : 'grey.900',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          cursor: toolOutput || toolInput ? 'pointer' : 'default',
        }}
        onClick={() => (toolOutput || toolInput) && setExpanded(!expanded)}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: needsApproval
              ? 'warning.main'
              : toolOutput
                ? 'success.main'
                : 'info.main',
            color: 'white',
            flexShrink: 0,
          }}
        >
          {needsApproval ? (
            <HandymanIcon fontSize="small" />
          ) : toolOutput ? (
            <CheckCircleRoundedIcon fontSize="small" />
          ) : (
            <HandymanIcon fontSize="small" />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: 'text.primary' }}
            >
              {toolName}
            </Typography>
          </Box>
          {needsApproval && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
            >
              {t('tools.approvalRequired')}
            </Typography>
          )}
        </Box>
      </Box>

      {needsApproval && approval ? (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <ToolApprovalPrompt
            approval={approval}
            toolInput={toolInput}
            onApprove={onApprove}
            onDeny={onDeny}
          />
        </Box>
      ) : (
        <Collapse in={expanded}>
          <Box
            sx={{
              px: 1.5,
              pb: 1.5,
              borderTop: (theme) =>
                `1px solid ${
                  theme.palette.mode === 'light'
                    ? theme.palette.grey[200]
                    : theme.palette.grey[800]
                }`,
            }}
          >
            {toolInput && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}
                >
                  {t('tools.inputParameters')}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
                    fontFamily: 'monospace',
                    color: 'text.primary',
                  }}
                >
                  {JSON.stringify(toolInput, null, 2)}
                </Box>
              </Box>
            )}

            {toolOutput && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}
                >
                  {t('tools.result')}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'success.lightest'
                        : 'rgba(76, 175, 80, 0.1)',
                    fontFamily: 'monospace',
                    color: 'text.primary',
                    border: (theme) =>
                      `1px solid ${
                        theme.palette.mode === 'light'
                          ? theme.palette.success.light
                          : 'rgba(76, 175, 80, 0.3)'
                      }`,
                  }}
                >
                  {JSON.stringify(toolOutput, null, 2)}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
